import type {
  AreaId,
  ApprovalId,
  PersonId,
  RequestId,
  SubAreaId,
  WorkItemId,
} from '@shared/brand';
import type { DateRange } from '../value-objects/DateRange';
import type { RequestStatus } from '../vocabulary';
import type { Auditable } from './Organization';

/**
 * A vacation request — the system's central record.
 *
 * Three things are stored rather than derived, each for a reason:
 *
 *  - `businessDays` is computed at submission and frozen. Recomputing it later
 *    would silently restate history the moment HR edits the holiday calendar,
 *    and an approved request's day count is a commitment.
 *  - `personName` / `subAreaName` are denormalised copies. The kanban board and
 *    the calendar render hundreds of rows; resolving each name would be an N+1
 *    read against a 500-person roster. The canonical foreign key sits right
 *    beside each copy, so the Postgres migration drops the copies and joins.
 *  - `areaId` is duplicated from the sub area because every security rule and
 *    every list query scopes by area, and Firestore cannot join to get it.
 */
/**
 * The backup's answer to being named.
 *
 * Spelled like `RequestApproval.decision` rather than in Spanish, because it is
 * the same kind of thing: a signature, pending until somebody gives it.
 */
export const REPLACEMENT_DECISIONS = ['pending', 'accepted', 'declined'] as const;
export type ReplacementDecision = (typeof REPLACEMENT_DECISIONS)[number];

export interface VacationRequest extends Auditable {
  readonly id: RequestId;
  /** Human-facing folio, e.g. `VC-0207`. Assigned server-side, never reused. */
  readonly folio: string;
  readonly personId: PersonId;
  readonly personName: string;
  readonly areaId: AreaId;
  readonly subAreaId: SubAreaId | null;
  readonly subAreaName: string;
  readonly range: DateRange;
  /** Frozen at submission — see above. */
  readonly businessDays: number;
  readonly replacementPersonId: PersonId | null;
  readonly replacementName: string;
  /**
   * Where the named backup stands on covering the absence.
   *
   * Null when nobody was named — whether that is allowed is the `reemplazo`
   * flag's business, not this field's. Naming somebody is not the same as their
   * agreeing to it, and until this says so the cover is an assumption: the
   * person who would actually carry the work has not been asked.
   */
  readonly replacementDecision: ReplacementDecision | null;
  /** Why they cannot cover it. Only ever set alongside `declined`. */
  readonly replacementReason: string | null;
  readonly replacementDecidedAt: Date | null;
  /**
   * Why a leader approved without the backup's confirmation.
   *
   * The escape hatch, and it leaves a mark on purpose. Somebody on leave or who
   * never signs in would otherwise hold a request hostage, so the leader can go
   * ahead — but «nadie confirmó que va a cubrirlo» has to stay readable on the
   * request afterwards, not just in the log.
   */
  readonly replacementWaivedReason: string | null;
  readonly status: RequestStatus;
  /**
   * La ruta en Storage del registro firmado en PDF, en las solicitudes que lo
   * traen.
   *
   * **Ya no se pide ni se sube** —7 oct 2026: el portal es ahora ese registro—,
   * así que en toda solicitud nueva es `null`, y `null` no significa que falte
   * nada. Se conserva porque las de antes sí lo traen, y ese archivo se puede
   * seguir abriendo desde el tablero.
   */
  readonly pdfPath: string | null;
  /** Free-text note from the requester. */
  readonly notes: string;
  /**
   * How many signatures this request needs, decided at submission by the
   * double-sign-off rule. Frozen so that turning the feature off midway does
   * not retroactively half-approve requests already in flight.
   */
  readonly requiredSignatures: 1 | 2;
  /** Set when rejected or cancelled; the catalogue text, copied for the record. */
  readonly resolutionReason: string | null;
  /**
   * When the request reached a final decision.
   *
   * Denormalised from the last `RequestApproval` deliberately. The reports
   * screen shows the average time from sending to hearing back, and deriving it
   * from the approvals would mean loading every approval of every request —
   * exactly the N+1 the rest of this model is shaped to avoid. Null while the
   * request is still open.
   */
  readonly decidedAt: Date | null;
  /** Folio of the request this one reschedules, for the handoff's reagenda flow. */
  readonly rescheduledFromFolio: string | null;
  /**
   * Quién la registró en nombre de la persona, o `null` si la pidió ella misma.
   *
   * No es adorno: quien abre su ficha y encuentra unos días que no pidió tiene
   * derecho a saber quién los puso ahí. Y distingue lo que el área registró —que
   * nace aprobada y sin formato firmado— de una solicitud normal a la que de
   * verdad le falta el papel.
   */
  readonly assignedByName: string | null;
  /**
   * Quién la registró, por id. `null` si la pidió su propia dueña.
   *
   * El nombre de arriba es para leerlo; esto es para **decidir**. Quien registró
   * unas vacaciones por cuenta ajena es el único que puede borrarlas, y comparar
   * nombres para eso sería atar un permiso a una cadena que cambia cuando alguien
   * se casa. Ver `canDeleteAssignedVacation`.
   *
   * El servidor lo escribe desde el 21 de agosto y **nadie lo leía**: no estaba
   * en esta entidad, ni en el codec, ni en ninguna pantalla. El comentario de
   * `assignedByName` decía que servía «para que en la ficha se distinga» y la
   * ficha no distinguía nada.
   */
  readonly assignedByPersonId: PersonId | null;
  readonly submittedAt: Date | null;
}

/**
 * One signature on a request.
 *
 * A row per decision instead of the prototype's `apr1` / `apr2` string pair:
 * it carries who, when and why, it supports a third signature without a schema
 * change, and it is an ordinary child table in Postgres. `order` is the position
 * in the approval chain, not a timestamp ordering.
 */
export interface RequestApproval extends Auditable {
  readonly id: ApprovalId;
  readonly requestId: RequestId;
  readonly approverPersonId: PersonId;
  readonly approverName: string;
  readonly order: 1 | 2;
  readonly decision: ApprovalDecision;
  readonly decidedAt: Date | null;
  /** Required by the handoff whenever the decision is a rejection. */
  readonly reason: string | null;
}

export const APPROVAL_DECISIONS = ['pending', 'approved', 'rejected'] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];

export const isApprovalDecision = (value: unknown): value is ApprovalDecision =>
  typeof value === 'string' && (APPROVAL_DECISIONS as readonly string[]).includes(value);

/**
 * A pending item the requester is handing over. Its own collection because the
 * list is user-controlled and unbounded — the prototype's embedded `work: []`
 * array would eventually push a request document toward Firestore's 1 MiB
 * limit, and it cannot be queried or normalised.
 */
export interface RequestWorkItem extends Auditable {
  readonly id: WorkItemId;
  readonly requestId: RequestId;
  /** Ticket reference, e.g. `IOS-482`. Optional: not every handover has one. */
  readonly ticket: string;
  readonly text: string;
}

/** Signatures collected so far, in chain order. */
/**
 * Si esta persona puede borrar estas vacaciones.
 *
 * **Dos condiciones, y las dos son necesarias.** Que la solicitud se haya
 * registrado por cuenta ajena —tiene autor— y que quien mira **sea** ese autor.
 *
 * No es el permiso de administrar ni el rol de superusuario: es la autoría. Un
 * superusuario no puede borrar lo que registró otro administrador, y eso es
 * deliberado — borrar no deja fila que mirar después, así que el único que puede
 * hacerlo es quien sabe por qué existía.
 *
 * Y **una solicitud que su dueña pidió no se borra nunca por aquí**, ni siquiera
 * por quien la aprobó: eso es cancelar, tiene su propia callable y deja constancia
 * de que existió. Borrar es para deshacer un registro que no debió hacerse.
 *
 * Vive en el dominio porque la aplican dos sitios: la pantalla, para decidir si
 * dibuja el botón, y el servidor, que es donde la regla se hace valer. Dos copias
 * de un `===` es como se acaba con un botón que se ve y una llamada que rechaza.
 */
export const canDeleteAssignedVacation = (
  request: Pick<VacationRequest, 'assignedByPersonId'>,
  viewerPersonId: PersonId,
): boolean => {
  const autor = request.assignedByPersonId;
  /*
   * Los dos vacíos se comprueban antes de comparar, y no es celo.
   *
   * `'' === ''` es cierto, así que un identificador ausente por los dos lados
   * —un documento anterior al campo y una sesión a medio armar— autorizaría a
   * cualquiera con un `!==` suelto. Es la misma guarda que `deleteVerdict` en el
   * servidor, que es donde la regla manda; aquí sólo decide si se dibuja el
   * botón.
   */
  if (autor === null || autor.length === 0) return false;
  if (viewerPersonId.length === 0) return false;
  return autor === viewerPersonId;
};

export const collectedSignatures = (approvals: readonly RequestApproval[]): RequestApproval[] =>
  approvals
    .filter((a) => a.decision === 'approved')
    .sort((a, b) => a.order - b.order);

/** True once the request has every signature it was created needing. */
export const isFullySigned = (
  request: Pick<VacationRequest, 'requiredSignatures'>,
  approvals: readonly RequestApproval[],
): boolean => collectedSignatures(approvals).length >= request.requiredSignatures;

/** The rejecting signature, if any — the handoff shows its reason on the card. */
export const rejectingApproval = (
  approvals: readonly RequestApproval[],
): RequestApproval | undefined => approvals.find((a) => a.decision === 'rejected');

/**
 * Whether the requester may still cancel. The handoff allows cancellation while
 * a request is in flight or approved but not yet taken, and always demands a
 * reason from the catalogue.
 */
export const isCancelable = (status: RequestStatus): boolean =>
  status === 'Por revisar' ||
  status === 'En revisión' ||
  status === 'Visto bueno' ||
  status === 'Aprobada';

/** Only a rejected request offers "Reagendar". */
export const isReschedulable = (status: RequestStatus): boolean => status === 'Rechazada';

/**
 * Formats a folio from a sequence number: `VC-0207`. Zero-padded to four
 * digits, and beyond 9999 it simply grows rather than wrapping — a folio must
 * stay unique for the life of the company.
 */
export const formatFolio = (prefix: string, sequence: number): string =>
  `${prefix}-${String(sequence).padStart(4, '0')}`;

export const VACATION_FOLIO_PREFIX = 'VC';
export const PERMISSION_FOLIO_PREFIX = 'PER';
export const INCAPACITY_FOLIO_PREFIX = 'INC';

/**
 * Whether the cover is still an assumption rather than an agreement.
 *
 * True while a named backup has not accepted — including when they said no,
 * because a refusal leaves the request with nobody covering it just as surely as
 * silence does. False when nobody was named at all: there is no one to wait for,
 * and the `reemplazo` flag is what decides whether that is acceptable.
 *
 * This is what the approve button reads, and the server re-checks it. A leader
 * can still go ahead by recording why, which is what clears it.
 */
export const awaitsReplacement = (
  request: Pick<
    VacationRequest,
    'replacementPersonId' | 'replacementDecision' | 'replacementWaivedReason'
  >,
): boolean =>
  request.replacementPersonId !== null &&
  request.replacementWaivedReason === null &&
  request.replacementDecision !== 'accepted';
